import React, { useEffect, useRef, useState } from "react";
import { SWIPE_GUARD_CLASS } from "@/utils/swipe";
import "./s.cursor_display.css";


// Binary-search the character index inside a text node whose midpoint is
// closest to viewport x. Returns an offset in [0, length].
function offsetFromX(textNode: Text, x: number, length: number): number {
    if (length === 0) return 0;
    const range = document.createRange();
    let lo = 0;
    let hi = length;
    while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        range.setStart(textNode, mid);
        range.setEnd(textNode, mid + 1);
        const rect = range.getBoundingClientRect();
        if (x < rect.left + rect.width / 2) hi = mid;
        else lo = mid + 1;
    }
    return lo;
}

function posFromX(
    x: number,
    caretRef: React.RefObject<HTMLSpanElement | null>,
    beforeRef: React.RefObject<HTMLSpanElement | null>,
    afterRef: React.RefObject<HTMLSpanElement | null>,
    value: string,
    cursorPos: number,
): number {
    const caretLeft = caretRef.current?.getBoundingClientRect().left ?? Infinity;
    if (x < caretLeft) {
        const node = beforeRef.current?.firstChild;
        return node?.nodeType === Node.TEXT_NODE
            ? offsetFromX(node as Text, x, cursorPos)
            : 0;
    } else {
        const node = afterRef.current?.firstChild;
        return node?.nodeType === Node.TEXT_NODE
            ? cursorPos + offsetFromX(node as Text, x, value.length - cursorPos)
            : value.length;
    }
}

// Multi-line aware position resolver. Uses caretRangeFromPoint when multiline
// so the Y coordinate determines the correct visual line.
function posFromPoint(
    x: number,
    y: number,
    caretRef: React.RefObject<HTMLSpanElement | null>,
    beforeRef: React.RefObject<HTMLSpanElement | null>,
    afterRef: React.RefObject<HTMLSpanElement | null>,
    value: string,
    cursorPos: number,
    multiline: boolean,
): number {
    if (multiline) {
        const range: Range | null = (() => {
            if ('caretRangeFromPoint' in document) {
                return (document as any).caretRangeFromPoint(x, y) as Range;
            }
            const pos = (document as any).caretPositionFromPoint?.(x, y);
            if (!pos) return null;
            const r = document.createRange();
            r.setStart(pos.offsetNode, pos.offset);
            return r;
        })();
        if (range) {
            const node = range.startContainer;
            const offset = range.startOffset;
            if (beforeRef.current && (beforeRef.current === node || beforeRef.current.contains(node))) {
                return node.nodeType === Node.TEXT_NODE ? Math.min(offset, cursorPos) : 0;
            }
            if (afterRef.current && (afterRef.current === node || afterRef.current.contains(node))) {
                return node.nodeType === Node.TEXT_NODE
                    ? Math.min(cursorPos + offset, value.length)
                    : value.length;
            }
        }
    }
    return posFromX(x, caretRef, beforeRef, afterRef, value, cursorPos);
}

// Resolve a global character offset from a viewport point using the browser's
// caret-from-point API. Unlike posFromX (which assumes a before/caret/after
// structure) this works for arbitrary spans — needed while a selection
// highlight splits the text into extra nodes. Returns null to fall back.
function offsetFromPointInContainer(container: HTMLElement, x: number, y: number): number | null {
    let node: Node | null = null;
    let offset = 0;
    if ('caretRangeFromPoint' in document) {
        const r = (document as any).caretRangeFromPoint(x, y) as Range | null;
        if (!r) return null;
        node = r.startContainer;
        offset = r.startOffset;
    } else if ((document as any).caretPositionFromPoint) {
        const pos = (document as any).caretPositionFromPoint(x, y);
        if (!pos) return null;
        node = pos.offsetNode;
        offset = pos.offset;
    } else {
        return null;
    }
    if (!node || node.nodeType !== Node.TEXT_NODE || !container.contains(node)) return null;
    // Masking preserves a 1:1 char count, so summed text length == value index.
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
    let total = 0;
    let n = walker.nextNode();
    while (n) {
        if (n === node) return total + offset;
        total += (n.textContent ?? "").length;
        n = walker.nextNode();
    }
    return null;
}

const LONG_PRESS_MS = 450;
const DRAG_THRESHOLD_PX = 6;

const MENU_W = 186;
const MENU_H = 36;
const MENU_MARGIN = 8;

export const CursorDisplay = (props: {
    value: string;
    cursorPos: number;
    /** Highlighted selection range. When selStart < selEnd the span is rendered
     *  as a selection; a tap collapses it to a caret at the tapped position. */
    selStart?: number;
    selEnd?: number;
    placeholder?: string;
    onCursorMove?: (pos: number) => void;
    onInsertText?: (text: string) => void;
    onSelectAll?: () => void;
    className?: string;
    password?: boolean;
    /** Number of visible lines. >1 enables wrapping and Y-aware cursor positioning. */
    lines?: number;
    style?: React.CSSProperties;
}) => {
    const beforeRef = useRef<HTMLSpanElement>(null);
    const caretRef = useRef<HTMLSpanElement>(null);
    const afterRef = useRef<HTMLSpanElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const magnifierScrollRef = useRef<HTMLDivElement>(null);
    const magnifierCaretRef = useRef<HTMLSpanElement>(null);

    // Always-current props ref — lets event handlers (registered once) read
    // the latest props without stale closure issues.
    const propsRef = useRef(props);
    propsRef.current = props;

    const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activePointerRef = useRef<number | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const lastPosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const magnifierActiveRef = useRef(false);
    const magnifierPosRef = useRef({ x: 0, y: 0 });
    const longPressTriggeredRef = useRef(false);
    const hasDraggedRef = useRef(false);

    // Tick to trigger re-renders when magnifier opens/closes/updates.
    const [, setTick] = useState(0);
    const bump = () => setTick(t => t + 1);

    const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

    // Password masking: track which index is temporarily revealed (last typed char).
    const [revealIndex, setRevealIndex] = useState<number | null>(null);
    const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const prevMaskRef = useRef({ len: props.value.length, cursor: props.cursorPos });

    useEffect(() => {
        if (!props.password) {
            if (revealTimerRef.current) { clearTimeout(revealTimerRef.current); revealTimerRef.current = null; }
            setRevealIndex(null);
            return;
        }
        const prev = prevMaskRef.current;
        prevMaskRef.current = { len: props.value.length, cursor: props.cursorPos };
        if (props.value.length > prev.len && props.cursorPos > prev.cursor) {
            const idx = props.cursorPos - 1;
            setRevealIndex(idx);
            if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
            revealTimerRef.current = setTimeout(() => {
                setRevealIndex(null);
                revealTimerRef.current = null;
            }, 1000);
        } else {
            if (revealTimerRef.current) { clearTimeout(revealTimerRef.current); revealTimerRef.current = null; }
            setRevealIndex(null);
        }
    }, [props.value, props.cursorPos, props.password]);

    useEffect(() => () => { if (revealTimerRef.current) clearTimeout(revealTimerRef.current); }, []);

    // Returns display string with masking applied. fullyMask=true (magnifier) never reveals.
    const maskText = (str: string, startIdx: number, fullyMask = false): string => {
        if (!props.password) return str.replace(/ /g, '\u00a0');
        return str.split('').map((ch, i) => {
            if (!fullyMask && revealIndex !== null && startIdx + i === revealIndex) {
                return ch === ' ' ? '\u00a0' : ch;
            }
            return '\u2022'; // •
        }).join('');
    };

    // Center magnifier viewport on the caret after every render.
    useEffect(() => {
        if (!magnifierActiveRef.current) return;
        const scrollEl = magnifierScrollRef.current;
        const caretEl = magnifierCaretRef.current;
        if (!scrollEl || !caretEl) return;
        const sr = scrollEl.getBoundingClientRect();
        const cr = caretEl.getBoundingClientRect();
        scrollEl.scrollLeft += cr.left - (sr.left + sr.width / 2);
    });

    useEffect(() => {
        caretRef.current?.scrollIntoView({ inline: "nearest", block: "nearest" });
    }, [props.cursorPos, props.value]);

    // Register native pointer listeners once so handlers always read the
    // freshest props via propsRef — no stale-closure issues.
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        // Resolve a tapped point to a caret position. While a selection is active
        // the before/caret/after structure doesn't hold, so use caret-from-point.
        const resolvePos = (x: number, y: number): number => {
            const { value, cursorPos, selStart, selEnd, lines } = propsRef.current;
            const selS = selStart ?? cursorPos;
            const selE = selEnd ?? cursorPos;
            if (selS < selE) {
                const off = offsetFromPointInContainer(el, x, y);
                if (off != null) return Math.max(0, Math.min(value.length, off));
            }
            return posFromPoint(x, y, caretRef, beforeRef, afterRef, value, cursorPos, (lines ?? 1) > 1);
        };

        const clearTimer = () => {
            if (longPressTimerRef.current !== null) {
                clearTimeout(longPressTimerRef.current);
                longPressTimerRef.current = null;
            }
        };

        const onDown = (e: PointerEvent) => {
            e.stopPropagation();
            if (activePointerRef.current !== null) return;
            activePointerRef.current = e.pointerId;
            touchStartRef.current = { x: e.clientX, y: e.clientY };
            lastPosRef.current = { x: e.clientX, y: e.clientY };
            longPressTriggeredRef.current = false;
            hasDraggedRef.current = false;

            longPressTimerRef.current = setTimeout(() => {
                longPressTimerRef.current = null;
                longPressTriggeredRef.current = true;
                if (hasDraggedRef.current) {
                    // Drag already in progress — activate magnifier now.
                    const { x, y } = lastPosRef.current;
                    magnifierPosRef.current = { x, y };
                    magnifierActiveRef.current = true;
                    el.setPointerCapture(e.pointerId);
                    propsRef.current.onCursorMove?.(resolvePos(x, y));
                }
                bump();
            }, LONG_PRESS_MS);
        };

        const onMove = (e: PointerEvent) => {
            if (activePointerRef.current !== e.pointerId) return;
            lastPosRef.current = { x: e.clientX, y: e.clientY };

            if (magnifierActiveRef.current) {
                // Magnifier is active — track position and update cursor.
                magnifierPosRef.current = { x: e.clientX, y: e.clientY };
                propsRef.current.onCursorMove?.(resolvePos(e.clientX, e.clientY));
                bump();
                return;
            }

            // Track whether the finger has drifted enough to count as a drag.
            if (!hasDraggedRef.current) {
                const start = touchStartRef.current;
                if (start) {
                    const dx = e.clientX - start.x;
                    const dy = e.clientY - start.y;
                    if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PX) {
                        hasDraggedRef.current = true;
                        // If long press already fired, activate magnifier immediately.
                        if (longPressTriggeredRef.current) {
                            magnifierPosRef.current = { x: e.clientX, y: e.clientY };
                            magnifierActiveRef.current = true;
                            el.setPointerCapture(e.pointerId);
                            propsRef.current.onCursorMove?.(resolvePos(e.clientX, e.clientY));
                            bump();
                        }
                    }
                }
            }
        };

        const onUp = (e: PointerEvent) => {
            if (activePointerRef.current !== e.pointerId) return;
            clearTimer();

            const wasMagnifier = magnifierActiveRef.current;
            const wasLongPressNoDrag = longPressTriggeredRef.current && !hasDraggedRef.current;

            magnifierActiveRef.current = false;
            activePointerRef.current = null;
            touchStartRef.current = null;
            longPressTriggeredRef.current = false;
            hasDraggedRef.current = false;

            if (wasLongPressNoDrag) {
                setContextMenu({ x: e.clientX, y: e.clientY });
            } else if (!wasMagnifier) {
                propsRef.current.onCursorMove?.(resolvePos(e.clientX, e.clientY));
            }
            bump();
        };

        // Prevent the browser context menu that fires on long-press.
        const onContextMenu = (e: Event) => e.preventDefault();

        el.addEventListener("pointerdown", onDown);
        el.addEventListener("pointermove", onMove);
        el.addEventListener("pointerup", onUp);
        el.addEventListener("pointercancel", onUp);
        el.addEventListener("contextmenu", onContextMenu);
        return () => {
            el.removeEventListener("pointerdown", onDown);
            el.removeEventListener("pointermove", onMove);
            el.removeEventListener("pointerup", onUp);
            el.removeEventListener("pointercancel", onUp);
            el.removeEventListener("contextmenu", onContextMenu);
        };
    }, []);

    const selStart = props.selStart ?? props.cursorPos;
    const selEnd = props.selEnd ?? props.cursorPos;
    const hasSelection = selStart < selEnd;

    const showMagnifier = magnifierActiveRef.current;
    const MAGNIFIER_W = 220;
    const MAGNIFIER_H = 52;
    const ARROW_H = 10;
    const MARGIN = 8;
    const mLeft = Math.max(MARGIN, Math.min(
        window.innerWidth - MAGNIFIER_W - MARGIN,
        magnifierPosRef.current.x - MAGNIFIER_W / 2,
    ));
    const mTop = magnifierPosRef.current.y - MAGNIFIER_H - ARROW_H - 16;

    const menuLeft = contextMenu
        ? Math.max(MENU_MARGIN, Math.min(window.innerWidth - MENU_W - MENU_MARGIN, contextMenu.x - MENU_W / 2))
        : 0;
    const menuTop = contextMenu ? contextMenu.y - MENU_H - 12 : 0;

    return (
        <>
            <div
                ref={containerRef}
                className={[props.className, (props.lines ?? 1) > 1 && 'cursor_display--multiline'].filter(Boolean).join(' ') || undefined}
                style={{ ...props.style, touchAction: "none" }}
            >
                {props.value ? (
                    hasSelection ? (
                        <>
                            <span className="cursor_display__text" ref={beforeRef}>{maskText(props.value.slice(0, selStart), 0)}</span>
                            <span className="cursor_display__selection">{maskText(props.value.slice(selStart, selEnd), selStart)}</span>
                            <span className="cursor_display__caret" ref={caretRef} />
                            <span className="cursor_display__text" ref={afterRef}>{maskText(props.value.slice(selEnd), selEnd)}</span>
                        </>
                    ) : (
                        <>
                            <span className="cursor_display__text" ref={beforeRef}>{maskText(props.value.slice(0, props.cursorPos), 0)}</span>
                            <span className="cursor_display__caret" ref={caretRef} />
                            <span className="cursor_display__text" ref={afterRef}>{maskText(props.value.slice(props.cursorPos), props.cursorPos)}</span>
                        </>
                    )
                ) : (
                    <>
                        <span className="cursor_display__caret" ref={caretRef} />
                        <span className="cursor_display__placeholder">{props.placeholder}</span>
                    </>
                )}
            </div>
            {showMagnifier && (
                <div
                    className={`cursor_display__magnifier ${SWIPE_GUARD_CLASS}`}
                    style={{ left: mLeft, top: mTop, width: MAGNIFIER_W, height: MAGNIFIER_H }}
                >
                    <div className="cursor_display__magnifier_scroll" ref={magnifierScrollRef}>
                        {props.value ? (
                            <>
                                <span className="cursor_display__mag_text">{maskText(props.value.slice(0, props.cursorPos), 0, true)}</span>
                                <span className="cursor_display__mag_caret" ref={magnifierCaretRef} />
                                <span className="cursor_display__mag_text">{maskText(props.value.slice(props.cursorPos), props.cursorPos, true)}</span>
                            </>
                        ) : (
                            <>
                                <span className="cursor_display__mag_caret" ref={magnifierCaretRef} />
                                <span className="cursor_display__mag_placeholder">{props.placeholder}</span>
                            </>
                        )}
                    </div>
                    <div className="cursor_display__magnifier_arrow" />
                </div>
            )}
            {contextMenu && !props.password && (
                <>
                    <div className="cursor_display__ctx_backdrop" onPointerDown={() => setContextMenu(null)} />
                    <div className="cursor_display__ctx_menu" style={{ left: menuLeft, top: menuTop, width: MENU_W }}>
                        <button className="cursor_display__ctx_item" onPointerDown={() => {
                            navigator.clipboard.writeText(props.value).catch(() => {});
                            setContextMenu(null);
                        }}>Copy</button>
                        {props.onInsertText && (
                            <>
                                <div className="cursor_display__ctx_divider" />
                                <button className="cursor_display__ctx_item" onPointerDown={() => {
                                    navigator.clipboard.readText().then(text => props.onInsertText!(text)).catch(() => {});
                                    setContextMenu(null);
                                }}>Paste</button>
                            </>
                        )}
                        {props.onSelectAll && (
                            <>
                                <div className="cursor_display__ctx_divider" />
                                <button className="cursor_display__ctx_item" onPointerDown={() => {
                                    props.onSelectAll!();
                                    setContextMenu(null);
                                }}>Select All</button>
                            </>
                        )}
                    </div>
                </>
            )}
        </>
    );
};
